/* eslint-disable max-classes-per-file */
import { observable, runInAction, action } from 'mobx';
import {
  DepartmentMaterialSetsDTO,
  MaterialSetDTO,
  UpdateMaterialSetDTO,
  CreateMaterialSetDTO,
  CreateCheckedGroupMaterialDTO,
  CreateCheckedMaterialSetGroupsDTO,
  CheckStatus,
  SetCheckDTO,
  SetType
} from 'dto/materialSet';
import {
  getMaterialSets,
  getMaterialSet,
  updateMaterialSet,
  createMaterialSet,
  getMaterialSetGuides,
  getMaterialSetChecks,
  postMaterialSetCheckItems,
  getMaterialSetCheckItems
} from 'api/materialSet';
import { createSetGroup, deleteSetGroup, updateSetGroup } from 'api/setGroup';
import { createTag, updateTag, deleteTag } from 'api/tag';
import { createGroupMaterial, deleteGroupMaterial } from 'api/groupMaterial';
import { GroupedMaterialKnowledgeDTO } from 'dto/materialKnowledge';
import { GroupedMaterialGuidesDTO, MaterialType } from 'dto/material';
import { CreateSetGroupDTO, SetGroupDTO, UpdateSetGroupDTO } from 'dto/setGroup';
import { CreateGroupMaterialDTO } from 'dto/groupMaterial';
import { TagDTO } from 'dto/tag';
import { generateQRCodePDF } from 'api/qr';
import { QRSize } from 'dto/qr';
import { IsString, IsUUID } from 'class-validator';
import { AnyMaterial } from './searchStore';
import LoadingStore from './loadingStore';

export class QRUrlOptions {
  @IsString()
  groupType!: string;

  @IsUUID()
  materialSetId!: string;
}

export default class MaterialSetsStore {
  @observable
  private loadingStore: LoadingStore;

  @observable
  groupedMaterialSets: DepartmentMaterialSetsDTO[] = [];

  @observable
  materialSetDetail?: MaterialSetDTO;

  @observable
  materialSetId = '';

  @observable
  knowledge: GroupedMaterialKnowledgeDTO[] = [];

  @observable
  guides: GroupedMaterialGuidesDTO[] = [];

  @observable
  chapterIdForNewKnowledge = '';

  @observable
  newKnowledgePosition?: number = undefined;

  @observable
  isSetGroupFormFlyoutOpen = false;

  @observable
  selectedSetGroup?: SetGroupDTO;

  @observable
  isCheckListVisible = false;

  @observable
  isCheckListFlyoutOpen = false;

  @observable
  materialSetCheckList: SetCheckDTO[] = [];

  @observable
  selectedSetCheck?: SetCheckDTO;

  @observable
  checkedSetGroupsToCreate: CreateCheckedMaterialSetGroupsDTO[] = [];

  @observable
  private lastMaterialSetsOptions?: {
    functionalAreaId: string;
    groupType: SetType;
  };

  constructor(loadingStore: LoadingStore) {
    this.loadingStore = loadingStore;
  }

  async loadMaterialSets(functionalAreaId: string, groupType: SetType) {
    const groupedMaterialSets = await this.loadingStore.withLoadingBar(() => getMaterialSets({ functionalAreaId, type: groupType }));
    runInAction(() => {
      this.groupedMaterialSets = groupedMaterialSets;
      this.lastMaterialSetsOptions = {
        functionalAreaId,
        groupType
      };
    });
  }

  async refreshMaterialSets() {
    if (!this.lastMaterialSetsOptions) {
      return;
    }
    this.loadMaterialSets(this.lastMaterialSetsOptions.functionalAreaId, this.lastMaterialSetsOptions.groupType);
  }

  @action
  clearMaterialSets() {
    this.groupedMaterialSets = [];
  }

  @action
  async loadMaterialSet(materialSetId: string, noReset = false) {
    if (!noReset) {
      this.materialSetDetail = undefined;
    }
    const materialSetDetail = await this.loadingStore.withLoadingBar(() => getMaterialSet(materialSetId));
    runInAction(() => {
      this.materialSetDetail = materialSetDetail;
      this.materialSetId = materialSetId;
    });
  }

  async refreshMaterialSet() {
    if (!this.materialSetId) {
      return;
    }
    this.loadMaterialSet(this.materialSetId, true);
  }

  async updateMaterialSet(materialSet: UpdateMaterialSetDTO) {
    const materialSetDetail = await this.loadingStore.withLoadingBar(() => updateMaterialSet(materialSet));
    runInAction(async () => {
      this.materialSetDetail = materialSetDetail;
    });
    this.refreshMaterialSets();
  }

  async createMaterialSet(materialSet: CreateMaterialSetDTO, functionalAreaId: string) {
    await this.loadingStore.withLoadingBar(() => createMaterialSet(materialSet));
    this.loadMaterialSets(functionalAreaId, materialSet.type);
  }

  async loadGuides(materialSetId: string) {
    const guides = await this.loadingStore.withLoadingBar(() => getMaterialSetGuides(materialSetId));
    runInAction(() => {
      this.guides = guides;
    });
  }

  async createSetGroup(group: CreateSetGroupDTO) {
    await this.loadingStore.withLoadingBar(() => createSetGroup(group));
    this.refreshMaterialSet();
  }

  async updateSetGroup(group: UpdateSetGroupDTO) {
    await this.loadingStore.withLoadingBar(() => updateSetGroup(group));
    this.refreshMaterialSet();
    this.refreshMaterialSets();
  }

  async deleteSetGroup(groupId: string) {
    await this.loadingStore.withLoadingBar(() => deleteSetGroup(groupId));
    this.refreshMaterialSet();
  }

  async deleteGroupMaterial(groupMaterialId: string) {
    await this.loadingStore.withLoadingBar(() => deleteGroupMaterial(groupMaterialId));
  }

  async createGroupMaterial(groupMaterial: CreateGroupMaterialDTO) {
    return this.loadingStore.withLoadingBar(() => createGroupMaterial(groupMaterial));
  }

  async loadMaterialSetChecks(materialSetId: string) {
    const materialSetCheckList = await this.loadingStore.withLoadingBar(() => getMaterialSetChecks(materialSetId));
    runInAction(() => {
      this.materialSetCheckList = materialSetCheckList;
    });
  }

  async submitMaterialSetChecks() {
    await this.loadingStore.withLoadingBar(() =>
      postMaterialSetCheckItems({
        materialSetId: this.materialSetId,
        setGroups: this.checkedSetGroupsToCreate
      })
    );
    this.setIsCheckListVisible(false);
  }

  async loadSelectedSetCheck(setCheckId: string) {
    const selectedSetCheck = await this.loadingStore.withLoadingBar(() => getMaterialSetCheckItems(setCheckId));
    runInAction(() => {
      if (this.materialSetDetail) {
        this.checkedSetGroupsToCreate = this.materialSetDetail.setGroups.map(group => {
          const groupMaterials: CreateCheckedGroupMaterialDTO[] = group.groupMaterials.map(groupMaterial => {
            const selectedGroupMaterial = selectedSetCheck.groupMaterialChecks.get(groupMaterial.groupMaterialId);
            return {
              groupMaterialId: groupMaterial.groupMaterialId,
              status: selectedGroupMaterial ? selectedGroupMaterial.status : CheckStatus.Missing
            };
          });
          const selectedSetGroup = selectedSetCheck.setGroupChecks.get(group.setGroupId);
          return {
            setGroupId: group.setGroupId,
            status: selectedSetGroup ? selectedSetGroup.status : CheckStatus.Missing,
            groupMaterials
          };
        });
      }
      this.selectedSetCheck = selectedSetCheck;
      this.isCheckListVisible = true;
    });
  }

  async createSetGroupTag(setGroupId: string, name: string) {
    const tag = await this.loadingStore.withLoadingBar(() => createTag({ setGroupId, name }));
    return tag;
  }

  async createGroupMaterialTag(groupMaterialId: string, name: string) {
    const tag = await this.loadingStore.withLoadingBar(() => createTag({ groupMaterialId, name }));
    return tag;
  }

  async updateTag(tag: TagDTO) {
    const updatedTag = await this.loadingStore.withLoadingBar(() => updateTag(tag));
    return updatedTag;
  }

  async deleteTag(tagId: string) {
    const result = await this.loadingStore.withLoadingBar(() => deleteTag(tagId));
    return result;
  }

  async getQRCodePDF(
    translation: {
      headingTop: string;
      description: string;
      heading2: string;
      fileName: string;
      size: QRSize;
    }[]
  ): Promise<{ url: string; fileName: string } | undefined> {
    const localUrl = await this.loadingStore.withLoadingBar(async () => {
      if (!this.materialSetDetail) {
        return undefined;
      }

      const type = SetType[this.materialSetDetail.type].toString().toLowerCase();

      const payload: QRUrlOptions = {
        groupType: type,
        materialSetId: this.materialSetDetail.materialSetId
      };

      const mappedTranslation = translation.map(t => {
        return {
          headingTop: t.headingTop,
          payload: JSON.stringify(payload),
          heading: this.materialSetDetail?.name || 'NONAME',
          description: t.description,
          heading2: t.heading2,
          size: t.size
        };
      });
      return generateQRCodePDF(mappedTranslation).then(pdf => {
        const blob = new Blob([pdf], { type: 'octet/stream' });
        return window.URL.createObjectURL(blob);
      });
    });

    const fileName = translation[0].fileName;
    if (localUrl) {
      return { url: localUrl, fileName };
    }

    return undefined;
  }

  @action
  setMaterialSetId(materialSetId: string) {
    this.materialSetId = materialSetId;
  }

  @action
  setIsSetGroupFormFlyoutOpen(status: boolean, selectedSetGroup?: SetGroupDTO) {
    this.isSetGroupFormFlyoutOpen = status;
    if (selectedSetGroup) {
      this.selectedSetGroup = selectedSetGroup;
    }
    if (!status) {
      this.selectedSetGroup = undefined;
    }
  }

  @action
  setIsCheckListVisible(isCheckListVisible: boolean) {
    runInAction(() => {
      this.isCheckListVisible = isCheckListVisible;
      this.selectedSetCheck = undefined;
    });
  }

  @action
  setIsCheckListFlyoutOpen(isCheckListFlyoutOpen: boolean) {
    this.isCheckListFlyoutOpen = isCheckListFlyoutOpen;
  }

  @action
  generateCheckItems() {
    if (this.materialSetDetail) {
      this.checkedSetGroupsToCreate = this.materialSetDetail.setGroups.map(group => {
        const groupMaterials: CreateCheckedGroupMaterialDTO[] = group.groupMaterials.map(groupMaterial => ({
          groupMaterialId: groupMaterial.groupMaterialId,
          status: CheckStatus.Missing
        }));
        return {
          setGroupId: group.setGroupId,
          status: CheckStatus.Missing,
          groupMaterials
        };
      });
    }
  }

  @action
  addSetGroupCheckItem(status: CheckStatus, setGroupId: string) {
    this.checkedSetGroupsToCreate = this.checkedSetGroupsToCreate.map(setGroup => {
      if (setGroup.setGroupId === setGroupId) {
        return {
          ...setGroup,
          status
        };
      }
      return setGroup;
    });
  }

  @action
  addGroupMaterialCheckItem(status: CheckStatus, groupMaterialId: string) {
    this.checkedSetGroupsToCreate = this.checkedSetGroupsToCreate.map(setGroup => {
      const groupMaterials: CreateCheckedGroupMaterialDTO[] = setGroup.groupMaterials.map(groupMaterial => {
        if (groupMaterial.groupMaterialId === groupMaterialId) {
          return {
            ...groupMaterial,
            status
          };
        }
        return groupMaterial;
      });
      return {
        ...setGroup,
        groupMaterials
      };
    });
  }

  addMaterialToMaterialSet(materialLike: AnyMaterial, setGroupId: string) {
    if (materialLike.material && materialLike.material.materialType === MaterialType.material) {
      return this.createGroupMaterial({
        materialId: materialLike.material.materialId,
        setGroupId,
        position: 0
      });
    }

    return Promise.resolve(undefined);
  }
}
